import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
} from '@mui/icons-material';
import { themeColors } from '../../utils/theme';
import type { S3PaginationMetadata } from '../../types/s3';

/**
 * Props for S3PaginationControls component
 */
export interface S3PaginationControlsProps {
  /** Pagination metadata with current page, page size, and total count */
  paginationMetadata?: S3PaginationMetadata;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Whether pagination controls should be disabled */
  disabled?: boolean;
}

/**
 * S3PaginationControls Component
 *
 * Displays pagination UI controls for S3 file browser with:
 * - First/Previous/Next/Last page navigation buttons
 * - Current page and total items display
 * - Page size selector dropdown
 * - Automatic hiding when total items < page size
 * - Responsive Material-UI design
 *
 * Controls are hidden when:
 * - No pagination metadata is provided
 * - Total count is less than or equal to page size
 *
 * @component
 * @returns {React.FC} The pagination controls component
 */
const S3PaginationControls: React.FC<S3PaginationControlsProps> = ({
  paginationMetadata,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200],
  disabled = false,
}) => {
  // Calculate pagination values
  const totalPages = useMemo(() => {
    if (!paginationMetadata) return 0;
    return Math.ceil(paginationMetadata.totalCount / paginationMetadata.pageSize);
  }, [paginationMetadata]);

  const currentPageNumber = useMemo(() => {
    if (!paginationMetadata) return 1;
    return paginationMetadata.currentPage + 1; // Convert 0-indexed to 1-indexed for display
  }, [paginationMetadata]);

  // Determine if pagination controls should be visible
  const shouldShowPagination = useMemo(() => {
    if (!paginationMetadata) return false;
    // Show pagination only if total items exceeds page size
    return paginationMetadata.totalCount > paginationMetadata.pageSize;
  }, [paginationMetadata]);

  // Calculate range of items shown on current page
  const itemsRange = useMemo(() => {
    if (!paginationMetadata || totalPages === 0) {
      return { start: 0, end: 0 };
    }
    const start = paginationMetadata.currentPage * paginationMetadata.pageSize + 1;
    const end = Math.min(
      (paginationMetadata.currentPage + 1) * paginationMetadata.pageSize,
      paginationMetadata.totalCount
    );
    return { start, end };
  }, [paginationMetadata, totalPages]);

  // Callback handlers
  const handleFirstPage = () => {
    if (onPageChange) {
      onPageChange(0); // Convert to 0-indexed for API
    }
  };

  const handlePreviousPage = () => {
    if (paginationMetadata && onPageChange && paginationMetadata.currentPage > 0) {
      onPageChange(paginationMetadata.currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationMetadata && onPageChange && currentPageNumber < totalPages) {
      onPageChange(paginationMetadata.currentPage + 1);
    }
  };

  const handleLastPage = () => {
    if (onPageChange && totalPages > 0) {
      onPageChange(totalPages - 1); // Convert to 0-indexed for API
    }
  };

  const handlePageSizeChange = (event: any) => {
    if (onPageSizeChange) {
      onPageSizeChange(parseInt(event.target.value, 10));
    }
  };

  // Determine which buttons should be disabled
  const isFirstPageDisabled = disabled || !paginationMetadata || paginationMetadata.currentPage === 0;
  const isPreviousPageDisabled = disabled || !paginationMetadata || paginationMetadata.currentPage === 0;
  const isNextPageDisabled = disabled || !paginationMetadata || currentPageNumber >= totalPages;
  const isLastPageDisabled = disabled || !paginationMetadata || currentPageNumber >= totalPages;

  // Hide pagination controls if not needed
  if (!shouldShowPagination) {
    return null;
  }

  return (
    <Box
      component="nav"
      aria-label="Pagination controls"
      role="navigation"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2,
        px: 2,
        bgcolor: themeColors.accent,
        borderTop: `1px solid ${themeColors.primary}20`,
        borderRadius: '0 0 8px 8px',
      }}
    >
      {/* Left section: Items info */}
      <Typography
        component="div"
        variant="body2"
        sx={{
          color: themeColors.textSecondary,
          minWidth: '150px',
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        Showing {itemsRange.start}-{itemsRange.end} of {paginationMetadata?.totalCount || 0}
      </Typography>

      {/* Center section: Navigation buttons */}
      <Stack
        direction="row"
        spacing={1}
        role="group"
        aria-label="Page navigation"
      >
        <Tooltip title="First page">
          <span>
            <IconButton
              size="small"
              onClick={handleFirstPage}
              disabled={isFirstPageDisabled}
              aria-label={`Go to first page (page 1 of ${totalPages})`}
              aria-disabled={isFirstPageDisabled}
              sx={{
                color: isFirstPageDisabled ? themeColors.textSecondary : themeColors.primary,
              }}
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Previous page">
          <span>
            <IconButton
              size="small"
              onClick={handlePreviousPage}
              disabled={isPreviousPageDisabled}
              aria-label={`Go to previous page (page ${Math.max(1, currentPageNumber - 1)} of ${totalPages})`}
              aria-disabled={isPreviousPageDisabled}
              sx={{
                color: isPreviousPageDisabled ? themeColors.textSecondary : themeColors.primary,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Typography
          component="span"
          variant="body2"
          sx={{
            color: themeColors.text,
            display: 'flex',
            alignItems: 'center',
            px: 1,
            minWidth: '80px',
            textAlign: 'center',
            justifyContent: 'center',
          }}
          aria-label={`Current page: ${currentPageNumber} of ${totalPages}`}
          aria-current="page"
        >
          Page {currentPageNumber} of {totalPages}
        </Typography>

        <Tooltip title="Next page">
          <span>
            <IconButton
              size="small"
              onClick={handleNextPage}
              disabled={isNextPageDisabled}
              aria-label={`Go to next page (page ${Math.min(totalPages, currentPageNumber + 1)} of ${totalPages})`}
              aria-disabled={isNextPageDisabled}
              sx={{
                color: isNextPageDisabled ? themeColors.textSecondary : themeColors.primary,
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Last page">
          <span>
            <IconButton
              size="small"
              onClick={handleLastPage}
              disabled={isLastPageDisabled}
              aria-label={`Go to last page (page ${totalPages} of ${totalPages})`}
              aria-disabled={isLastPageDisabled}
              sx={{
                color: isLastPageDisabled ? themeColors.textSecondary : themeColors.primary,
              }}
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Right section: Page size selector */}
      <FormControl size="small" sx={{ minWidth: '120px' }}>
        <InputLabel
          sx={{
            color: themeColors.text,
            '&.Mui-focused': {
              color: themeColors.primary,
            },
          }}
          id="page-size-select-label"
        >
          Per page
        </InputLabel>
        <Select
          labelId="page-size-select-label"
          id="page-size-select"
          value={paginationMetadata?.pageSize || 50}
          onChange={handlePageSizeChange}
          disabled={disabled}
          label="Per page"
          aria-label="Items per page"
          aria-describedby="page-size-description"
          sx={{
            color: themeColors.text,
            bgcolor: 'transparent',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: themeColors.primary,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: themeColors.primary,
              opacity: 0.8,
            },
            '& .MuiSvgIcon-root': {
              color: themeColors.primary,
            },
          }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size} aria-label={`${size} items per page`}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default S3PaginationControls;
